import type { TodoTask } from "./types.ts";

/**
 * Renders one task list's tasks to JSON (ARCHITECTURE.md §7: `tasks/<list>.json`).
 *
 * Pure and dependency-free, unlike export.ts — kept separate so it is
 * unit-testable under plain Node (ARCHITECTURE.md's established pattern for
 * every module: logic that doesn't touch Graph/auth lives away from
 * auth/config.ts's import.meta.env read).
 */

export interface RenderedTask {
    title: string;
    status: string;
    importance: string;
    due: string | null;
    completed: string | null;
    created: string | null;
    lastModified: string | null;
    notes: string | null;
}

export function renderTask(task: TodoTask): RenderedTask {
    return {
        title: task.title ?? "",
        status: task.status ?? "notStarted",
        importance: task.importance ?? "normal",
        due: task.dueDateTime?.dateTime ?? null,
        completed: task.completedDateTime?.dateTime ?? null,
        created: task.createdDateTime ?? null,
        lastModified: task.lastModifiedDateTime ?? null,
        notes: task.body?.content ?? null
    };
}

/**
 * Sorted by creation date, falling back to title — a stable order across
 * repeat runs rather than whatever order Graph happened to return, so a
 * re-exported file diffs cleanly against the previous one.
 */
export function renderTaskListJson(tasks: readonly TodoTask[]): string {
    const rendered = tasks.map(renderTask);
    rendered.sort((a, b) => (a.created ?? "").localeCompare(b.created ?? "") || a.title.localeCompare(b.title));
    return JSON.stringify(rendered, null, 2);
}
